if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          viewerWrap: this.querySelector('.product-media-gallery__viewer-wrap'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mainPrevious: this.querySelector('[data-gallery-main-previous]'),
          mainNext: this.querySelector('[data-gallery-main-next]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        [this.elements.mainPrevious, this.elements.mainNext].forEach((button) => {
          button?.addEventListener('click', this.onMainNavigationClick.bind(this), true);
        });
        this.mql.addEventListener?.('change', this.updateMainNavigation.bind(this));
        this.bindGalleryDrag();

        this.updateMainNavigation();
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        const initialMedia = this.getMainMediaItems().find((element) => element.classList.contains('is-active'));
        if (initialMedia) this.syncActiveMedia(initialMedia);
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        if (!this.getAllMainMediaItems().includes(currentElement)) return;
        this.syncActiveMedia(currentElement);

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updateMainNavigation();
      }

      onMainNavigationClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const mediaItems = this.getMainMediaItems();
        if (mediaItems.length < 2) return;

        const currentIndex = this.getCurrentMainIndex(mediaItems);
        const direction = event.currentTarget.hasAttribute('data-gallery-main-next') ? 1 : -1;
        const nextIndex = Math.min(Math.max(currentIndex + direction, 0), mediaItems.length - 1);

        if (nextIndex === currentIndex) return;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      bindGalleryDrag() {
        const surface = this.elements.viewerWrap;
        if (!surface) return;

        this.galleryDrag = null;
        this.gallerySuppressClickUntil = 0;

        surface.addEventListener('pointerdown', this.onGalleryPointerDown.bind(this), { passive: false });
        surface.addEventListener('pointermove', this.onGalleryPointerMove.bind(this), { passive: false });
        surface.addEventListener('pointerup', this.onGalleryPointerUp.bind(this), { passive: false });
        surface.addEventListener('pointercancel', this.onGalleryPointerCancel.bind(this), { passive: false });
        surface.addEventListener('lostpointercapture', this.onGalleryPointerCancel.bind(this), { passive: false });

        // The theme's generic slider also listens for mouse/touch drags on the
        // inner list. Product media uses the pointer gesture above instead.
        ['mousedown', 'mousemove', 'mouseup', 'mouseleave', 'touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach(
          (eventName) => {
            surface.addEventListener(eventName, this.stopLegacyGalleryGesture.bind(this), true);
          }
        );
        surface.addEventListener('click', this.preventGalleryClickAfterDrag.bind(this), true);
        surface.addEventListener('dragstart', (event) => event.preventDefault(), true);
      }

      isGalleryControlTarget(target) {
        return Boolean(
          target &&
            typeof target.closest === 'function' &&
            target.closest('[data-gallery-main-previous], [data-gallery-main-next]')
        );
      }

      stopLegacyGalleryGesture(event) {
        if (this.isGalleryControlTarget(event.target)) return;
        event.stopPropagation();
      }

      onGalleryPointerDown(event) {
        if (this.isGalleryControlTarget(event.target)) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        const mediaItems = this.getMainMediaItems();
        if (mediaItems.length < 2) return;

        this.galleryDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          startIndex: this.getCurrentMainIndex(mediaItems),
          started: false,
          axis: null,
          startTime: Date.now()
        };
      }

      onGalleryPointerMove(event) {
        const drag = this.galleryDrag;
        if (!drag || drag.pointerId !== event.pointerId || drag.axis === 'vertical') return;

        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        const deltaX = event.clientX - drag.startX;
        const deltaY = event.clientY - drag.startY;

        if (!drag.started) {
          if (Math.abs(deltaX) < 7 && Math.abs(deltaY) < 7) return;
          if (Math.abs(deltaY) > Math.abs(deltaX) * 1.08) {
            drag.axis = 'vertical';
            return;
          }

          drag.axis = 'horizontal';
          drag.started = true;
          this.elements.viewerWrap.classList.add('is-gallery-dragging');
          try {
            this.elements.viewerWrap.setPointerCapture(event.pointerId);
          } catch (error) {}
        }

        if (drag.axis !== 'horizontal') return;
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
      }

      onGalleryPointerUp(event) {
        const drag = this.galleryDrag;
        if (!drag || drag.pointerId !== event.pointerId) return;

        const deltaX = drag.lastX - drag.startX;
        const duration = Math.max(1, Date.now() - drag.startTime);
        const threshold = Math.max(12, Math.min(36, this.elements.viewerWrap.clientWidth * 0.045));
        const quickSwipe = Math.abs(deltaX) >= 10 && Math.abs(deltaX) / duration >= 0.08;

        if (drag.started && drag.axis === 'horizontal') {
          if (event.cancelable) event.preventDefault();
          event.stopPropagation();

          const mediaItems = this.getMainMediaItems();
          let nextIndex = drag.startIndex;
          if (Math.abs(deltaX) >= threshold || quickSwipe) {
            nextIndex = deltaX < 0 ? drag.startIndex + 1 : drag.startIndex - 1;
            nextIndex = Math.max(0, Math.min(nextIndex, mediaItems.length - 1));
          }

          if (nextIndex !== drag.startIndex && mediaItems[nextIndex]) {
            this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
          }
          this.gallerySuppressClickUntil = Date.now() + 360;
        }

        this.finishGalleryDrag(event.pointerId);
      }

      onGalleryPointerCancel(event) {
        if (!this.galleryDrag) return;
        this.finishGalleryDrag(event.pointerId);
      }

      finishGalleryDrag(pointerId) {
        const surface = this.elements.viewerWrap;
        try {
          if (surface.hasPointerCapture(pointerId)) surface.releasePointerCapture(pointerId);
        } catch (error) {}
        surface.classList.remove('is-gallery-dragging');
        this.galleryDrag = null;
      }

      preventGalleryClickAfterDrag(event) {
        if (Date.now() >= this.gallerySuppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      getAllMainMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(
          this.elements.viewer.querySelectorAll('[id^="Slider-Gallery-"] > li[data-media-id]')
        );
      }

      getMainMediaItems() {
        const mediaItems = this.getAllMainMediaItems().filter(
          (element) => !element.hasAttribute('data-variant-hidden')
        );

        if (!this.elements.thumbnails) return mediaItems;

        // Follow the visible thumbnail order. Variant/gallery scripts can reorder
        // thumbnails independently from the original product media DOM order.
        const mediaById = new Map(mediaItems.map((element) => [element.dataset.mediaId, element]));
        const orderedItems = Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]'))
          .filter((thumbnail) => !thumbnail.hasAttribute('data-variant-hidden'))
          .map((thumbnail) => mediaById.get(thumbnail.dataset.target))
          .filter(Boolean);
        const orderedSet = new Set(orderedItems);

        return orderedItems.concat(mediaItems.filter((element) => !orderedSet.has(element)));
      }

      syncActiveMedia(activeMedia) {
        this.getAllMainMediaItems().forEach((element) => {
          const isActive = element === activeMedia;
          element.classList.toggle('is-active', isActive);
          if (isActive) {
            element.style.setProperty('display', 'block', 'important');
          } else {
            element.style.removeProperty('display');
          }
        });
      }

      getCurrentMainIndex(mediaItems) {
        const activeMedia = mediaItems.find((element) => element.classList.contains('is-active'));
        if (activeMedia) return mediaItems.indexOf(activeMedia);

        const slider = this.elements.viewer?.slider;
        if (!slider || mediaItems.length < 2) return 0;

        const slideOffset = mediaItems[1].offsetLeft - mediaItems[0].offsetLeft;
        if (!slideOffset) return 0;
        return Math.min(Math.max(Math.round(slider.scrollLeft / slideOffset), 0), mediaItems.length - 1);
      }

      updateMainNavigation() {
        const previousButton = this.elements.mainPrevious;
        const nextButton = this.elements.mainNext;
        if (!previousButton || !nextButton) return;

        const mediaItems = this.getMainMediaItems();
        if (mediaItems.length < 2) {
          previousButton.setAttribute('disabled', 'disabled');
          nextButton.setAttribute('disabled', 'disabled');
          return;
        }

        const currentIndex = this.getCurrentMainIndex(mediaItems);

        previousButton.toggleAttribute('disabled', currentIndex === 0);
        nextButton.toggleAttribute('disabled', currentIndex >= mediaItems.length - 1);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.getMainMediaItems().find((element) => element.dataset.mediaId === mediaId);
        if (!activeMedia) {
          return;
        }
        this.syncActiveMedia(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Recalculate the mobile slider after changing the active slide. This keeps
        // the first arrow click from briefly landing on an empty scroll position.
        if (!prepend && this.elements.viewer.slider && !this.mql.matches) {
          this.elements.viewer.resetPages();
        }

        this.updateMainNavigation();

        this.preventStickyHeader();
        window.requestAnimationFrame(() => {
          const mainSlider = activeMedia.parentElement;
          if (mainSlider && !this.mql.matches) {
            mainSlider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
